import posthog from "posthog-js";

/**
 * Errors the browser hands to PostHog's error tracking, beyond what
 * posthog-js's own `capture_exceptions` already catches (uncaught errors and
 * unhandled rejections, frontend.tsx).
 *
 * The gap that leaves is every failed request: each page catches its own
 * fetch error and shows it as a message, so nothing is ever uncaught and
 * nothing is ever reported. Wrapping fetch once covers every page, including
 * ones written later, rather than asking 28 call sites to remember.
 *
 * Only server errors (5xx) and requests that never got a response. A 4xx is
 * the API answering a bad request -- an unreadable upload, an unknown token --
 * which the page already explains to the visitor, and which would bury the
 * real failures in volume.
 *
 * Everything here is a no-op until PostHog has initialised, which on dev
 * (no keys) is never.
 */

/** Report one error, with whatever describes where it happened. */
export function captureError(error: unknown, properties: Record<string, unknown> = {}) {
  if (!posthog.__loaded) return;
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    posthog.captureException(err, { surface: "frontend", ...properties });
  } catch {
    // Reporting must never be the thing that breaks the page.
  }
}

// Tokens, content keys and ids in a path would make every request its own
// error in PostHog; the message is what it groups on, so they come out of it.
// The full URL still goes along as a property.
function pathTemplate(pathname: string): string {
  return pathname
    .split("/")
    .map((segment) => (/\d/.test(segment) && segment.length >= 8) || /^\d+$/.test(segment) ? ":id" : segment)
    .join("/");
}

let installed = false;

/**
 * Wrap window.fetch so failed requests are reported. `ignoreOrigin` is
 * PostHog's own host: its requests failing is not a site error, and
 * reporting it through the thing that just failed would loop.
 */
export function installFetchErrorTracking(ignoreOrigin: string) {
  if (installed) return;
  installed = true;
  const ignored = new URL(ignoreOrigin).origin;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const request = input instanceof Request ? input : null;
    const url = new URL(request ? request.url : String(input), window.location.href);
    const method = (init?.method ?? request?.method ?? "GET").toUpperCase();
    if (url.origin === ignored) return originalFetch(input, init);

    const describe = {
      http_method: method,
      http_url: url.href,
      http_path: url.pathname,
      page_path: window.location.pathname,
    };

    let response: Response;
    try {
      response = await originalFetch(input, init);
    } catch (err) {
      // An abort is the page cancelling its own request, not a failure.
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        const error = new Error(`Network error: ${method} ${pathTemplate(url.pathname)}`);
        captureError(error, {
          ...describe,
          network_error: err instanceof Error ? err.message : String(err),
          online: navigator.onLine,
        });
      }
      throw err;
    }

    if (response.status >= 500) {
      // Read from a clone, off the caller's path: the page gets its response
      // untouched and unblocked, and the report follows when the body arrives.
      const status = response.status;
      response
        .clone()
        .text()
        .catch(() => "")
        .then((body) => {
          let detail: unknown = body.slice(0, 1000);
          try {
            detail = JSON.parse(body).detail ?? detail;
          } catch {
            // Not JSON: a proxy's error page, say. Keep the text.
          }
          captureError(new Error(`HTTP ${status}: ${method} ${pathTemplate(url.pathname)}`), {
            ...describe,
            http_status: status,
            response_detail: detail,
          });
        });
    }
    return response;
  };
}
